import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LockOutlined, RollbackOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import {
  verifyBiometrics,
  triggerSecurityHaptic,
  disableBiometrics,
  SECURITY_EVENTS,
  getAutoLockTimeoutMs,
  notifySecurityChanged,
} from '../../utils/webauthn';
import './Security.css';

export const AppLock: React.FC = () => {
  const { t } = useTranslation();
  const [isLocked, setIsLocked] = useState(false);
  const [savedPin, setSavedPin] = useState<string | null>(null);
  const [hasBiometrics, setHasBiometrics] = useState(false);
  const [autoLockSetting, setAutoLockSetting] = useState('immediately');
  const [pin, setPin] = useState('');
  const [isError, setIsError] = useState(false);
  const [isBioScanning, setIsBioScanning] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Synchronized refs to avoid stale closures in event listeners
  const isLockedRef = useRef(isLocked);
  isLockedRef.current = isLocked;
  const isBioScanningRef = useRef(isBioScanning);
  isBioScanningRef.current = isBioScanning;
  const hasBiometricsRef = useRef(hasBiometrics);
  hasBiometricsRef.current = hasBiometrics;
  const savedPinRef = useRef(savedPin);
  savedPinRef.current = savedPin;

  // Background and activity tracking refs
  const lastHiddenTimeRef = useRef<number | null>(null);
  const lastActivityTimeRef = useRef<number>(Date.now());

  // Refresh config dynamically when settings change
  const refreshConfig = useCallback(() => {
    if (typeof window === 'undefined') return;
    const stored = localStorage.getItem('security-pin');
    const bio = localStorage.getItem('security_biometrics') === 'true';
    const delay = localStorage.getItem('security_autolock') || 'immediately';
    const isValid = Boolean(stored && stored.length === 4);

    setSavedPin(isValid ? stored : null);
    savedPinRef.current = isValid ? stored : null;
    setHasBiometrics(bio && isValid);
    hasBiometricsRef.current = bio && isValid;
    setAutoLockSetting(delay);

    if (!isValid) {
      setIsLocked(false);
      isLockedRef.current = false;
    }
  }, []);

  // Biometric verification handler
  const handleBiometricUnlock = useCallback(async () => {
    if (!hasBiometricsRef.current || isBioScanningRef.current) return;
    try {
      setIsBioScanning(true);
      isBioScanningRef.current = true;
      const ok = await verifyBiometrics();
      if (ok) {
        setIsLocked(false);
        isLockedRef.current = false;
        setPin('');
        setIsError(false);
        lastActivityTimeRef.current = Date.now();
      }
    } catch {
      // User cancelled or failed biometric prompt; stay on PIN screen
    } finally {
      setIsBioScanning(false);
      isBioScanningRef.current = false;
    }
  }, []);

  // Initial load and global event subscriptions
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const stored = localStorage.getItem('security-pin');
    const bio = localStorage.getItem('security_biometrics') === 'true';
    const delay = localStorage.getItem('security_autolock') || 'immediately';

    if (stored && stored.length === 4) {
      setSavedPin(stored);
      savedPinRef.current = stored;
      setIsLocked(true);
      isLockedRef.current = true;
      setHasBiometrics(bio);
      hasBiometricsRef.current = bio;
    } else {
      setIsLocked(false);
      isLockedRef.current = false;
    }
    setAutoLockSetting(delay);
    setIsInitialized(true);

    const onSecurityChanged = () => {
      refreshConfig();
    };

    const onLockNow = () => {
      const pinNow = localStorage.getItem('security-pin');
      if (pinNow && pinNow.length === 4) {
        setIsLocked(true);
        isLockedRef.current = true;
        setPin('');
        setIsError(false);
      }
    };

    window.addEventListener(SECURITY_EVENTS.CHANGED, onSecurityChanged);
    window.addEventListener(SECURITY_EVENTS.LOCK_NOW, onLockNow);
    window.addEventListener('storage', onSecurityChanged);

    return () => {
      window.removeEventListener(SECURITY_EVENTS.CHANGED, onSecurityChanged);
      window.removeEventListener(SECURITY_EVENTS.LOCK_NOW, onLockNow);
      window.removeEventListener('storage', onSecurityChanged);
    };
  }, [refreshConfig]);

  // Bank-grade Auto-lock: Screen off / app backgrounding lifecycle
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleVisibilityChange = () => {
      const currentPin = localStorage.getItem('security-pin');
      const isConfigured = Boolean(currentPin && currentPin.length === 4);
      if (!isConfigured) return;

      const currentDelay = localStorage.getItem('security_autolock') || 'immediately';

      if (document.visibilityState === 'hidden') {
        // App minimized or phone screen locked
        const now = Date.now();
        lastHiddenTimeRef.current = now;
        try {
          sessionStorage.setItem('wt_last_hidden_time', now.toString());
        } catch {
          // ignore
        }

        // Bank standard: if auto-lock is 'immediately', lock while backgrounded
        // to immediately protect view in task switcher / screen wake
        if (currentDelay === 'immediately') {
          setIsLocked(true);
          isLockedRef.current = true;
          setPin('');
          setIsError(false);
        }
      } else if (document.visibilityState === 'visible') {
        // User unlocked phone or switched back to app
        const hiddenAt =
          lastHiddenTimeRef.current ||
          Number(sessionStorage.getItem('wt_last_hidden_time') || 0);

        let shouldLock = isLockedRef.current;
        if (hiddenAt > 0) {
          const elapsed = Date.now() - hiddenAt;
          const timeoutMs = getAutoLockTimeoutMs(currentDelay);

          if (elapsed >= timeoutMs) {
            shouldLock = true;
            setIsLocked(true);
            isLockedRef.current = true;
            setPin('');
            setIsError(false);
          }
        }
        lastHiddenTimeRef.current = null;
        try {
          sessionStorage.removeItem('wt_last_hidden_time');
        } catch {
          // ignore
        }

        // Trigger biometrics smoothly once screen is active
        const bio = localStorage.getItem('security_biometrics') === 'true';
        if (shouldLock && bio && !isBioScanningRef.current) {
          setTimeout(() => {
            if (isLockedRef.current && !isBioScanningRef.current) {
              handleBiometricUnlock();
            }
          }, 300);
        }
      }
    };

    const handlePageHide = () => {
      const currentPin = localStorage.getItem('security-pin');
      if (currentPin && currentPin.length === 4) {
        const now = Date.now();
        lastHiddenTimeRef.current = now;
        try {
          sessionStorage.setItem('wt_last_hidden_time', now.toString());
        } catch {
          // ignore
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [handleBiometricUnlock]);

  // Active user interaction tracking: Touching, scrolling, clicking, typing
  // Guarantees that while the user is actively working with the app, it NEVER locks or interrupts!
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onUserActivity = () => {
      lastActivityTimeRef.current = Date.now();
    };

    const activityEvents = ['touchstart', 'touchmove', 'click', 'keydown', 'wheel', 'scroll'];
    activityEvents.forEach((evt) => {
      window.addEventListener(evt, onUserActivity, { passive: true });
    });

    // Check foreground idle timeout every 15s.
    // Standard: Only locks if user is completely idle for 10 full minutes of zero touch/clicks.
    const FOREGROUND_IDLE_TIMEOUT_MS = 10 * 60 * 1000;
    const idleCheckInterval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (isLockedRef.current || isBioScanningRef.current) return;

      const currentPin = localStorage.getItem('security-pin');
      if (!currentPin || currentPin.length !== 4) return;

      const idleTime = Date.now() - lastActivityTimeRef.current;
      if (idleTime >= FOREGROUND_IDLE_TIMEOUT_MS) {
        setIsLocked(true);
        isLockedRef.current = true;
        setPin('');
        setIsError(false);
      }
    }, 15000);

    return () => {
      activityEvents.forEach((evt) => {
        window.removeEventListener(evt, onUserActivity);
      });
      clearInterval(idleCheckInterval);
    };
  }, []);

  // Auto-prompt biometrics once when app locks while visible
  useEffect(() => {
    if (isInitialized && hasBiometrics && isLocked) {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      const timer = setTimeout(() => {
        if (!isBioScanningRef.current && isLockedRef.current) {
          handleBiometricUnlock();
        }
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isInitialized, hasBiometrics, isLocked, handleBiometricUnlock]);

  const handleDigit = (digit: string) => {
    if (pin.length >= 4) return;
    triggerSecurityHaptic('light');

    const nextPin = pin + digit;
    setPin(nextPin);

    if (nextPin.length === 4) {
      const activePin = savedPinRef.current || localStorage.getItem('security-pin');
      if (nextPin === activePin) {
        triggerSecurityHaptic('success');
        setIsLocked(false);
        isLockedRef.current = false;
        setPin('');
        setIsError(false);
        lastActivityTimeRef.current = Date.now();
      } else {
        triggerSecurityHaptic('error');
        setIsError(true);
        setTimeout(() => {
          setPin('');
          setIsError(false);
        }, 500);
      }
    }
  };

  const handleBackspace = () => {
    triggerSecurityHaptic('light');
    setPin((prev) => prev.slice(0, -1));
    setIsError(false);
  };

  const handleResetPin = () => {
    Modal.confirm({
      title: t('Reset PIN'),
      content: t('Are you sure you want to reset PIN?'),
      okText: t('Reset PIN'),
      cancelText: t('Cancel'),
      okButtonProps: { danger: true },
      onOk: () => {
        localStorage.removeItem('security-pin');
        disableBiometrics();
        notifySecurityChanged();
        setIsLocked(false);
        isLockedRef.current = false;
        setPin('');
        setSavedPin(null);
        savedPinRef.current = null;
      },
    });
  };

  if (!isInitialized || !isLocked) {
    return null;
  }

  return (
    <div className="kf-applock-overlay">
      <div className="kf-applock-icon">
        <LockOutlined />
      </div>

      <div className="kf-applock-title">
        {t('Wallet access')}
      </div>

      <div className="kf-applock-subtitle">
        {hasBiometrics
          ? t('Use biometrics or enter PIN')
          : t('Enter PIN')}
      </div>

      {/* 4 PIN Dots */}
      <div className={`kf-pin-dots ${isError ? 'error' : ''}`}>
        {[0, 1, 2, 3].map((idx) => (
          <div
            key={idx}
            className={`kf-pin-dot ${pin.length > idx ? 'filled' : ''}`}
          />
        ))}
      </div>

      {/* 0-9 Numpad */}
      <div className="kf-numpad">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
          <button
            key={digit}
            type="button"
            className="kf-numpad-btn"
            onClick={() => handleDigit(digit)}
          >
            {digit}
          </button>
        ))}

        {hasBiometrics ? (
          <button
            type="button"
            className="kf-numpad-btn kf-bio-btn"
            onClick={handleBiometricUnlock}
            title={t('Biometrics')}
          >
            👆
          </button>
        ) : (
          <div />
        )}

        <button
          type="button"
          className="kf-numpad-btn"
          onClick={() => handleDigit('0')}
        >
          0
        </button>

        <button
          type="button"
          className="kf-numpad-btn kf-back-btn"
          onClick={handleBackspace}
          title="Backspace"
        >
          <RollbackOutlined />
        </button>
      </div>

      <div className="kf-applock-footer">
        <button
          type="button"
          className="kf-reset-btn"
          onClick={handleResetPin}
        >
          {t('Forgot PIN?')}
        </button>
      </div>
    </div>
  );
};

export default AppLock;
